import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { AvatarType } from './avatar-options';
import { isString } from '@core/utils/string';
import { Avatar } from '@core/models/common/avatar';

/**
 * Lets you create an avatar based on provided config.
 * If the user object contains the image url then that is used to display avatar
 * If not, the initials are extracted from name or email as the next option.
 *
 * @export
 * @class AvatarComponent
 * @implements {OnChanges}
 */
@Component({
    selector: 'pay-avatar',
    templateUrl: './avatar.component.html',
    styleUrls: ['./avatar.component.scss'],
    imports: [CommonModule, NgOptimizedImage],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        '[class.is-connected]': 'renderConnectionStatus && avatarType === AvatarType.CONNECTED',
        '[class.is-linked]': 'renderConnectionStatus && avatarType === AvatarType.LINKED',
        '[class.is-unlinked]': 'renderConnectionStatus && avatarType === AvatarType.UNLINKED',
        'class': 'c-avatar'
    }
})
export class AvatarComponent implements OnInit, OnChanges {

    /**
     * The user object to use for extracting information
     *
     * @type {object}@memberof AvatarComponent
     */
    @Input() user: Avatar;

    /**
     * Specific name to be used for extracting initials
     *
     * @type {string}@memberof AvatarComponent
     */
    @Input() name?: string;

    /**
     * If the image field inside user object has any specific prefix to avatar_url
     * pass it under this field
     *
     * @type {string}@memberof AvatarComponent
     */
    @Input() fieldPrefix?: string;

    AvatarType = AvatarType;
    isIconImage: boolean = false;
    initials: string = '';

    /**
     * Type of avatar to display. Can be 'unlinked' pulled from user.
     */
    public avatarType: AvatarType = AvatarType.CONNECTED;

    @Input()
    public imgField: string = 'avatar_url';

    @Input()
    public isSingleInitial: boolean = false;

    @Input()
    public renderConnectionStatus: boolean = true;

    private nameField: string = 'name';

    constructor(private cdr: ChangeDetectorRef) {
    }

    ngOnInit() {
        if (this.user) {
            this.setIconType();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['user'] && !changes['user'].firstChange) {
            this.setIconType();
        }
        if (changes['name'] && !changes['name'].firstChange) {
            this.setIconType();
        }
    }

    private setIconType(): void {

        // Define valid image field
        if (this.fieldPrefix) {
            this.imgField = this.fieldPrefix + '_' + this.imgField;
        }

        // Use the avatar url to display in avatar
        if (this.user && this.user.hasOwnProperty(this.imgField)
          && isString(this.user[this.imgField]) && this.user[this.imgField].length > 0) {

            this.isIconImage = true;
        } else {
            // If image not available extract the initials from name/email
            let initials = '';
            // Define initials based on user
            if (this.name) {
                this.nameField = 'name';

            } else if (this.fieldPrefix) {
                this.nameField = this.fieldPrefix + '_' + this.nameField;
            }

            let resolvedInitials = '';
            let parts = this.getPartsForType(this.nameField);
            if (parts.length === 1 || this.isSingleInitial) {
                if (parts[0]?.length > 0) {
                    resolvedInitials = parts[0].charAt(0).toUpperCase();
                }
            } else if (parts.length > 1) {
                if (parts[0].length > 0) {
                    resolvedInitials = parts[0].charAt(0).toUpperCase();
                }
                if (parts[parts.length - 1].length > 0) {
                    resolvedInitials += parts[parts.length - 1].charAt(0).toUpperCase();
                }
            }

            this.isIconImage = false;
            this.initials = resolvedInitials;
        }

        if ((this.user?.hasOwnProperty('onboard') && !(this.user as any).onboard) ||
          ((this.user?.hasOwnProperty('status') && (this.user as any).status !== 'connected')) ||
          ((this.user?.hasOwnProperty('connected') && !(this.user as any).connected))) {
            this.avatarType = AvatarType.UNLINKED;
        } else {
            this.avatarType = AvatarType.CONNECTED;
        }
        this.cdr.markForCheck();
    }

    /**
     * Extracts the initials from either the name if available
     * or the email as the next option
     *
     * @private
     * @param {string} userType
     * @returns {string[]}
     * @memberof AvatarComponent
     */
    private getPartsForType(userType: string): string[] {
        let res = [];
        if (userType.indexOf('name') !== -1) {
            if (this.user) {
                if (isString(this.user[userType]) && this.user[userType] !== '') {
                    res = this.user[userType].split(' ');
                } else {
                    // Add support for name as integer or null
                    res = [(this.user[userType] || 'Untitled') + ''];
                }
            }
        } else {
            // email
            if (this.user && this.user[userType]
              && typeof this.user[userType] === 'string'
              && this.user[userType].indexOf('@') !== -1) {
                let mailName = this.user[userType].split('@')[0];
                res = mailName.split('.');

            } else {
                res = ['Untitled'];
            }
        }

        return res;
    }
}
